import { useEffect, useRef, useState } from 'react';
import styles from './ClosedBetsFilter.module.css';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { START_YEAR } from 'config/constants';
import { SlipStatus } from 'types/enums';
import { CLOSED_BETS_DEFAULT_DAYS } from 'config/constants';
import { getEndOfDay } from 'helpers/dateHelper';

interface Props {
  getSlips: (
    _startDate: Date,
    _endDate: Date,
    _betStatus: SlipStatus,
    _onlyLiveBets?: boolean
  ) => void;
}

export const ClosedBetsFilter = ({ getSlips }: Props) => {
  const [showForm, setShowForm] = useState(true);
  // Closed bets open on the last 7 days (MyBets loads that range first).
  const [selectedPeriod, setSelectedPeriod] = useState<string>(
    String(CLOSED_BETS_DEFAULT_DAYS)
  );
  const [selectedStatus, setSelectedStatus] = useState<string>('0');
  const [startMonth, setStartMonth] = useState<number | null>(null);
  const [startYear, setStartYear] = useState<number | null>(null);
  const [endMonth, setEndMonth] = useState<number | null>(null);
  const [endYear, setEndYear] = useState<number | null>(null);
  const [showOnlyLiveBets, setShowOnlyLiveBets] = useState<boolean>(false);
  const { t } = useTranslation();

  const filterClosedBets = async () => {
    let startDate = new Date();
    startDate.setHours(0, 0);
    let endDate = getEndOfDay();

    if (selectedPeriod === 'custom_date') {
      // The list loads once both months are picked.
      if (!startMonth || !startYear || !endMonth || !endYear) return;
      startDate = new Date(startYear, startMonth - 1, 1);
      endDate = new Date(endYear, endMonth, 0);
    } else if (selectedPeriod !== '0') {
      startDate.setDate(startDate.getDate() - Number.parseInt(selectedPeriod));
    }

    let status = SlipStatus.CLOSED;
    switch (selectedStatus) {
      case 'winning':
        status = SlipStatus.WINNING;
        break;
      case 'failed':
        status = SlipStatus.FAILED;
        break;
      case 'cancelled':
        status = SlipStatus.CANCELLED;
        break;
      case 'returned':
        status = SlipStatus.RETURNED;
    }
    getSlips(startDate, endDate, status, showOnlyLiveBets);
  };

  // Every change reloads the list at once - no "Show" button to press. The
  // first render is skipped: MyBets has already loaded the default range.
  const loaded = useRef(false);
  useEffect(() => {
    if (!loaded.current) {
      loaded.current = true;
      return;
    }
    filterClosedBets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPeriod,
    selectedStatus,
    startMonth,
    startYear,
    endMonth,
    endYear,
    showOnlyLiveBets,
  ]);

  return (
    <>
      <div
        className={styles.showFormButton}
        onClick={() => setShowForm(!showForm)}
      >
        <div
          className={classNames(
            styles.icon,
            showForm ? styles.minusSign : styles.plusSign
          )}
        />
        {t('search_label')}
      </div>
      {showForm && (
        <div className={styles.closedBetsFormWrapper}>
          <div className={styles.formInputValue}>
            <span>{t('select_bet_by_status_label')}:</span>
            <select
              onChange={(event) => {
                setSelectedStatus(event.target.value);
              }}
            >
              <option value="0">{t('show_all_bets_label')}</option>
              <option value="winning">{t('won_bets_label')}</option>
              <option value="failed">{t('lost_bets_label')}</option>
              <option value="cancelled">{t('cancelled_bets_label')}</option>
              <option value="returned">{t('returned_bets_label')}</option>
            </select>
          </div>
          <div className={styles.formInputValue}>
            <span>{t('select_period_label')}:</span>
            <select
              value={selectedPeriod}
              onChange={(event) => {
                setSelectedPeriod(event.target.value);
              }}
            >
              <option value="0">{t('today_label')}</option>
              <option value="3">{t('last_3_days_label')}</option>
              <option value="7">{t('last_7_days_label')}</option>
              <option value="14">{t('last_14_days_label')}</option>
              <option value="30">{t('last_30_days_label')}</option>
              <option value="60">{t('last_60_days_label')}</option>
              <option value="custom_date">{t('another_period_label')}</option>
            </select>
          </div>
          {selectedPeriod === 'custom_date' && (
            <>
              <div className={styles.formInputValue}>
                <span>{t('start_date_label')}:</span>
                <div className={styles.datePicker}>
                  <select
                    onChange={(event) =>
                      setStartMonth(Number.parseInt(event.target.value))
                    }
                  >
                    <option selected disabled>
                      {t('month_label')}
                    </option>
                    {[...Array(12).keys()].map((number) => (
                      <option value={number + 1} key={number}>
                        {number + 1}
                      </option>
                    ))}
                  </select>
                  <select
                    onChange={(event) =>
                      setStartYear(Number.parseInt(event.target.value))
                    }
                  >
                    <option selected disabled>
                      {t('year_label')}
                    </option>
                    {getAvailableYearOptions()}
                  </select>
                </div>
              </div>
              <div className={styles.formInputValue}>
                <span>{t('end_date_label')}:</span>
                <div className={styles.datePicker}>
                  <select
                    onChange={(event) =>
                      setEndMonth(Number.parseInt(event.target.value))
                    }
                  >
                    <option selected disabled>
                      {t('month_label')}
                    </option>
                    {[...Array(12).keys()].map((number) => (
                      <option value={number + 1}>{number + 1}</option>
                    ))}
                  </select>
                  <select
                    onChange={(event) =>
                      setEndYear(Number.parseInt(event.target.value))
                    }
                  >
                    <option selected disabled>
                      {t('year_label')}
                    </option>
                    {getAvailableYearOptions()}
                  </select>
                </div>
              </div>
            </>
          )}
          <div className={styles.formInputValue}>
            <div
              className={styles.checkboxWrapper}
              onClick={() => setShowOnlyLiveBets(!showOnlyLiveBets)}
            >
              <div
                className={classNames(
                  'checkmark-circle-icon-left',
                  styles.checkmark
                )}
                data-background={!showOnlyLiveBets && 'none'}
              />
              <span>{t('show_only_live_bets_label')}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const getAvailableYearOptions = () => {
  const years = [START_YEAR];
  for (let i = START_YEAR + 1; i <= new Date().getFullYear(); ++i) {
    years.push(i);
  }

  return years.map((number) => <option value={number}>{number}</option>);
};
